// 英雄选择界面：选1名英雄 + 选A/B队伍 → 组装4人tuple → 开始游戏
import { useState, useMemo } from "react";
import { useGameStore } from "@/store/gameStore";
import { filterPhase1Heroes } from "@/data/loader";
import { init as sfxInit } from "@/audio/sfx";
import type { Hero } from "@/types/heroes";
import { Rng } from "@/utils/rng";
import "@/styles/effects.css";

export default function HeroSelect() {
  const data = useGameStore((s) => s.data);
  const newGame = useGameStore((s) => s.newGame);
  const [picked, setPicked] = useState<Hero | null>(null);
  const [team, setTeam] = useState<"A" | "B">("A");

  const heroes = useMemo(
    () => (data ? filterPhase1Heroes(data.heroes) : []),
    [data]
  );

  if (!data) return null;

  function handleStart() {
    if (!picked || !data) return;
    sfxInit(); // 首次用户手势初始化音频
    const rng = new Rng(Date.now());
    const others = rng.shuffle(
      heroes.filter((h) => h.name !== picked.name)
    );
    const teamHeroes: [Hero, Hero, Hero, Hero] =
      team === "A"
        ? [picked, others[0], others[1], others[2]] // 人类 seat 0 (A队)
        : [others[0], picked, others[1], others[2]]; // 人类 seat 1 (B队)
    newGame({
      heroes: teamHeroes,
      humanSeat: team === "A" ? 0 : 1,
      deck: data.deck,
      seed: Date.now() % 100000,
    });
  }

  return (
    <div className="hero-select">
      <h1 className="hero-select__title">命运2 · 选择英雄</h1>
      <div className="hero-select__grid">
        {heroes.map((h) => (
          <div
            key={h.name}
            className={`hero-card${picked?.name === h.name ? " hero-card--selected" : ""}`}
            onClick={() => setPicked(h)}
            title={h.skills.map((s) => `${s.name}: ${s.description}`).join("\n")}
          >
            <img
              className="hero-card__portrait"
              src={h.portrait}
              alt={h.name}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
            <div className="hero-card__info">
              <div className="hero-card__name">{h.name}</div>
              <div className="hero-card__stats">
                <span>HP {h.hp}</span>
                <span>MP {h.mp}</span>
                <span>攻 {h.attack}</span>
              </div>
              <div className="hero-card__skills">
                {h.skills.map((s) => (
                  <div key={s.name} className="hero-card__skill">
                    {s.name}
                    {s.manaCost ? `(${s.manaCost}魔)` : ""}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hero-select__bottom">
        <div className="hero-select__team">
          <span className="hero-select__team-label">选择队伍：</span>
          <button
            className={`team-btn${team === "A" ? " team-btn--active team-btn--a" : ""}`}
            onClick={() => setTeam("A")}
            disabled={!picked}
          >
            A 队（先手）
          </button>
          <button
            className={`team-btn${team === "B" ? " team-btn--active team-btn--b" : ""}`}
            onClick={() => setTeam("B")}
            disabled={!picked}
          >
            B 队（后手）
          </button>
        </div>
        <button
          className="hero-select__start"
          disabled={!picked}
          onClick={handleStart}
        >
          开始游戏
        </button>
      </div>
    </div>
  );
}
